/**
 * Asking the browser, now and then, whether there is a newer build.
 *
 * Nothing here downloads or applies anything: sw.ts calls `skipWaiting` and
 * `clientsClaim`, so a worker that is found takes over at once and the next
 * launch draws the new bundle. What was missing was the looking. A registration
 * check happens when a page loads, and an installed app is *resumed* far more
 * often than it is launched — an iPhone can keep the same page alive for days.
 *
 * That is not a theory: it is how this was noticed. Two devices were still
 * filing socket-error rows by a rule that had been replaced eleven hours
 * earlier, which is eleven hours of a fix existing and not being anywhere near
 * the people it was for.
 */

/**
 * Often enough that a build put out in the morning is on the phones by lunch,
 * rarely enough that a person switching between the chat and something else all
 * day is not asking about it every time. The check is a conditional request
 * against one small file, and the browser skips it entirely if it made one
 * recently itself.
 */
const CHECK_EVERY_MS = 60 * 60_000;

let lastCheckedAt = 0;

/**
 * Checks for a new worker whenever the app comes back on screen, at most once
 * an hour. Called once, from main.tsx.
 */
export function watchForUpdates(): void {
  if (!('serviceWorker' in navigator)) return;

  // The load that is running this code registered the worker a moment ago,
  // which is a check; the clock starts from there rather than from zero.
  lastCheckedAt = Date.now();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;

    const now = Date.now();
    if (now - lastCheckedAt < CHECK_EVERY_MS) return;
    lastCheckedAt = now;

    // `ready` rather than `getRegistration`: on a first launch the worker is
    // still installing, and there is nothing to ask about until it is not.
    void navigator.serviceWorker.ready
      .then((registration) => registration.update())
      // Offline, most likely — the app is meant to work that way, and the next
      // time it is looked at is soon enough to try again.
      .catch(() => {});
  });
}
